import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Stack,
  useToast,
  Badge,
  Switch,
  IconButton,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useFieldArray,
  useForm,
  FormProvider,
  useWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type {
  GuidedFlowDraft,
  GuidedEditPayload,
} from "./components/CreateCampaignModal";
import { type BudgetData } from "./components/BudgetSelector";
import Container from "./components/Container";
import { FacebookPage, InstagramAccount } from "./types";
import actions from "../../../../../actions";
import { FaChevronLeft } from "react-icons/fa";
import CreateCampaignReview from "./CreateCampaignReview";
import CreativeBlockEditor from "./components/CreativeBlockEditor";
import GuidedBudgetEditor from "./components/GuidedBudgetEditor";
import GuidedScheduleEditor from "./components/GuidedScheduleEditor";
import AudienceCard from "./components/AudienceCard";
import CreativeAdsetTargetingCard from "./guidedFlow/components/CreativeAdsetTargetingCard";
import GuidedTargetingFields from "./guidedFlow/components/GuidedTargetingFields";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import dayjs from "dayjs";
import { wireScheduleToEventWall } from "./utils/guidedScheduleEventWall";
import {
  CreateCampaignFlowSchema,
  CreateCampaignFlowValues,
  hasManualAudienceTargetingSignal,
  PRIMARY_TEXT_MAX,
} from "./forms/campaignFlowForm";
import KYDLoading from "src/ui/KYDLoading";
import { loadingMessageToText } from "../util";
import { useCreateCampaignFlowState } from "./guidedFlow/state";
import {
  buildCreateCampaignPayload,
  buildSaveAdsetParams,
} from "./guidedFlow/payloads";
import CreateCampaignFlowPreview from "./guidedFlow/components/Preview";
import {
  hasMixedGuidedCreativeMediaTypes,
  MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
} from "./utils/validateCreativeMediaType";
import type {
  CreativeBlock,
  GuidedCreateCampaignParams,
  GuidedSubmitParams,
} from "./guidedFlow/types";

type GuidedLaunchResult = {
  campaignId: string;
  publishUpdateId?: string | null;
  publishAttempted?: boolean;
};

export type GuidedCampaignFlowProps = {
  draft: GuidedFlowDraft;
  adAccounts: { id: string; name: string }[];
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  pixels: { id: string; name: string }[];
  isRelatedDataLoading: boolean;
  createUploadUrl: (
    file: File,
    callback: (params: {
      signedUrl?: string;
      fileKey?: string;
      file_key?: string;
      cdn_url?: string;
    }) => void
  ) => void;
  onBack: () => void;
  onLaunchSuccess: (result?: GuidedLaunchResult | null) => void;
  publishStatusText?: string | null;
  toast: ReturnType<typeof useToast>;
  /**
   * Fetch Instagram accounts for one Facebook page (user picked a page, or the
   * flow applied a default first page). Parent should merge into `instagramAccounts`
   * and return the rows for the creative editor.
   */
  onFetchInstagramForFacebookPage?: (
    facebookPageId: string
  ) => void | Promise<void | InstagramAccount[]>;
  onGuidedCreateAdAccountResolved?: (ad_account_id: string) => void;
  handleCreateCampaign: (
    params: GuidedCreateCampaignParams
  ) =>
    | GuidedLaunchResult
    | null
    | void
    | Promise<GuidedLaunchResult | null | void>;
  /** Unified submit callback for edit/update/new-adset/new-creative modes. */
  handleSubmitGuidedEdit?: (params: GuidedSubmitParams) => void | Promise<void>;
  /** Search for locations and interests (used when Smart Targeting is off). */
  search?: {
    interests: (
      query: string
    ) => Promise<{ data: { id: string; name: string }[] }>;
    geo_markets: (query: string) => Promise<{
      data: {
        key: string;
        name: string;
        country_code?: string;
        country_name?: string;
      }[];
    }>;
  } | null;
};

interface CampaignDefaults {
  event_id: string;
  ad_account_id: string;
  pixel_id: string;
  campaign_name: string;
  primary_text_templates: string[];
  creative_images: { src: string; type: "square" | "portrait" }[];
  budget: {
    type: "lifetime" | "daily";
    amount: number;
    lifetime: number;
  };
  schedule: {
    start_at: string;
    end_at: string;
  };
  targeting: {
    strategy: "template" | "custom";
    resolved_strategies: { id: string; name: string; description: string }[];
    static_interests?: { id: string; name?: string }[];
  };
  instagram_id?: string;
  fb_page_id?: string;
}

function resolveGuidedEditPayload(
  draft: GuidedFlowDraft
): GuidedEditPayload | null {
  switch (draft.mode) {
    case "edit":
    case "edit_new_adset":
      return draft.guidedEditPayload;
    case "edit_new_creative":
      return draft.guidedEditPayload ?? null;
    case "create":
    default:
      return null;
  }
}

function payloadHasGeoLocations(
  loc:
    | {
        countries?: unknown[];
        geo_markets?: unknown[];
        regions?: unknown[];
        cities?: unknown[];
      }
    | undefined
): boolean {
  if (!loc) return false;
  return (
    (loc.countries?.length ?? 0) > 0 ||
    (loc.geo_markets?.length ?? 0) > 0 ||
    (loc.regions?.length ?? 0) > 0 ||
    (loc.cities?.length ?? 0) > 0
  );
}

/**
 * When true, turn off "Smart Targeting" so {@link GuidedTargetingFields} and custom audience UI render.
 */
function guidedEditPayloadImpliesManualTargeting(
  p: GuidedEditPayload
): boolean {
  const t = p.targeting;
  if (t.strategy === "custom") return true;
  if (payloadHasGeoLocations(t.locations)) return true;
  if ((t.custom_audiences?.length ?? 0) > 0) return true;
  if ((t.interests?.length ?? 0) > 0) return true;
  const d = t.demographics;
  if (d) {
    if (d.age_min != null && d.age_min !== 18) return true;
    if (d.age_max != null && d.age_max !== 65) return true;
    if (d.genders != null && d.genders !== "" && d.genders !== "all") {
      return true;
    }
  }
  return false;
}

function guidedEditHydrationKey(draft: GuidedFlowDraft): string {
  switch (draft.mode) {
    case "edit":
      return `e:${draft.campaignId}:${draft.adsetId}`;
    case "edit_new_adset":
      return `na:${draft.campaignId}`;
    case "edit_new_creative":
      return `nc:${draft.campaignId}:${
        draft.guidedEditPayload?.adset_id ?? ""
      }`;
    default:
      return "";
  }
}

/** Instagram actor ids on the guided-edit payload (deduped). */
function guidedEditPayloadInstagramIds(payload: GuidedEditPayload): string[] {
  const ids = new Set<string>();
  for (const c of payload.creatives ?? []) {
    const id = c.instagram_id?.trim();
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

function buildReviewEvent(event: GuidedFlowDraft["event"]): {
  name?: string;
  display_start_at?: string;
  venue?: { name?: string; address?: string };
  image?: string;
} | null {
  if (!event) return null;

  const reviewEvent: {
    name?: string;
    display_start_at?: string;
    venue?: { name?: string; address?: string };
    image?: string;
  } = {
    name: event.name,
    display_start_at: event.display_start_at,
    image: event.image,
  };

  if (event.venue) {
    reviewEvent.venue = {
      name: event.venue.name,
      address: event.venue.address,
    };
  }

  return reviewEvent;
}

const selectContainerStyles = {
  container: () => ({
    position: "relative" as const,
    width: "100%",
    backgroundColor: "white",
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 140,
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

function SectionHeader({
  title,
  subtitle,
  isComplete,
  isCollapsed,
  onToggle,
}: {
  title: React.ReactNode;
  subtitle?: string;
  isComplete?: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="full"
      py={2}
      onClick={onToggle}
      _hover={{ bg: "gray.50" }}
      rounded="md"
    >
      <HStack justify="space-between" align="center" w="full">
        <HStack align="center" spacing={2} flex={1}>
          {isComplete ? (
            <Icon
              as={CheckCircleIcon}
              color="green.500"
              boxSize={"20px"}
              flexShrink={0}
            />
          ) : (
            <Icon
              as={CheckCircleIcon}
              color="gray.400"
              boxSize={"20px"}
              flexShrink={0}
            />
          )}
          <Box>
            <Text fontSize="20px" fontWeight="semibold" lineHeight={"20px"}>
              {title}
            </Text>
            {subtitle && (
              <Text fontSize="sm" color="gray.600" fontWeight="normal">
                {subtitle}
              </Text>
            )}
          </Box>
        </HStack>
        <IconButton
          aria-label={isCollapsed ? "Expand" : "Collapse"}
          variant="ghost"
          size="sm"
          icon={
            isCollapsed ? (
              <ChevronDownIcon fontSize="lg" />
            ) : (
              <ChevronUpIcon fontSize="lg" />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
    </Box>
  );
}

export default function GuidedCampaignFlow({
  draft,
  adAccounts,
  pages,
  instagramAccounts,
  isRelatedDataLoading,
  createUploadUrl,
  onBack,
  onLaunchSuccess,
  publishStatusText,
  toast,
  onFetchInstagramForFacebookPage,
  onGuidedCreateAdAccountResolved,
  handleCreateCampaign,
  handleSubmitGuidedEdit,
  search,
}: GuidedCampaignFlowProps) {
  /** When set, the user is renaming this creative; value is the current input. */
  const [editingCreativeName, setEditingCreativeName] = useState<{
    index: number;
    value: string;
  } | null>(null);
  const [defaults, setDefaults] = useState<CampaignDefaults | null>(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [
    hasCompletedInitialDefaultsFetch,
    setHasCompletedInitialDefaultsFetch,
  ] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [budgetUpdatedByUser, setBudgetUpdatedByUser] = useState(false);
  const {
    sectionVisibility,
    isEditMode,
    showAudienceSection,
    showBudgetSection,
    isNewCreativeMode,
    creativeTargetAdsets,
    selectableCreativeTargetAdsetIds,
    adCreativeOpen,
    setAdCreativeOpen,
    audienceOpen,
    setAudienceOpen,
    budgetOpen,
    setBudgetOpen,
    mobilePanel,
    setMobilePanel,
    step,
    setStep,
    isExitConfirmOpen,
    setIsExitConfirmOpen,
    addToAllCreativeAdsets,
    setAddToAllCreativeAdsets,
    selectedCreativeTargetAdsetIds,
    setSelectedCreativeTargetAdsetIds,
    hasRequiredCreativeTargetAdsets,
    handleBackToEditor,
  } = useCreateCampaignFlowState({ draft });

  const uploadRefs = useRef<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >({});
  const applyingDefaultsBudgetRef = useRef(false);
  const hasHydratedDefaultsRef = useRef(false);
  const [instagramPageFetchLoading, setInstagramPageFetchLoading] =
    useState(false);

  const guidedEditPayload = resolveGuidedEditPayload(draft);
  const editHydrationKey = guidedEditHydrationKey(draft);

  const validationContext = useMemo(
    () => ({
      requireAudienceSection: showAudienceSection,
      requireBudgetSection: showBudgetSection,
      eventTimezone: draft.eventTimezone,
    }),
    [showAudienceSection, showBudgetSection, draft.eventTimezone]
  );

  const form = useForm<CreateCampaignFlowValues>({
    resolver: yupResolver(CreateCampaignFlowSchema),
    context: validationContext,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      creatives: [
        {
          primaryText: "",
          fbPage: null,
          instagramAccount: null,
        },
      ],
      smart_targeting: true,
      custom_audiences: [],
      audience_search_query: "",
      countries: [],
      geo_markets: [],
      regions: [],
      cities: [],
      age_min: 18,
      age_max: 65,
      genders: "all",
      interests: [],
      targeting_query: "",
      targeting_subtype: null,
      budget: {
        type: "lifetime",
        amount: {
          amount: null,
          currency: "USD",
        },
      },
      schedule: {
        start_at: "",
        end_at: "",
      },
    },
  });

  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "creatives",
  });

  const creatives = useWatch({ control, name: "creatives" }) ?? [];
  const customAudiences = useWatch({ control, name: "custom_audiences" }) ?? [];
  const interests = useWatch({ control, name: "interests" }) ?? [];
  const countries = useWatch({ control, name: "countries" }) ?? [];
  const geoMarkets = useWatch({ control, name: "geo_markets" }) ?? [];
  const regions = useWatch({ control, name: "regions" }) ?? [];
  const cities = useWatch({ control, name: "cities" }) ?? [];
  const ageMin = useWatch({ control, name: "age_min" });
  const ageMax = useWatch({ control, name: "age_max" });
  const genders = useWatch({ control, name: "genders" });
  const smartTargeting = useWatch({ control, name: "smart_targeting" });
  /** Smart targeting only applies in create mode when the toggle is on; edit flow always uses manual targeting UI. */
  const isSmartTargetingAvailable = !isEditMode && !!smartTargeting;
  const budget = useWatch({ control, name: "budget" });
  const schedule = useWatch({ control, name: "schedule" });

  const scheduleStart = schedule?.start_at ?? "";
  const scheduleEnd = schedule?.end_at ?? "";

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    void trigger("schedule");
  }, [draft.eventTimezone, trigger]);

  const budgetCents =
    budget?.amount?.amount != null
      ? Math.round(budget.amount.amount * 100)
      : undefined;
  const [debouncedBudgetCents, setDebouncedBudgetCents] = useState<
    number | undefined
  >(budgetCents);
  const [debouncedBudgetType, setDebouncedBudgetType] = useState<
    "daily" | "lifetime"
  >(budget?.type === "daily" ? "daily" : "lifetime");

  useEffect(() => {
    const nextBudgetType = budget?.type === "daily" ? "daily" : "lifetime";

    if (!budgetUpdatedByUser) {
      setDebouncedBudgetCents(budgetCents);
      setDebouncedBudgetType(nextBudgetType);
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedBudgetCents(budgetCents);
      setDebouncedBudgetType(nextBudgetType);
    }, 500); // 500ms debounce time for budget changes

    return () => clearTimeout(timeout);
  }, [budgetUpdatedByUser, budgetCents, budget?.type]);

  const createUploadUrlForKYD = useCallback(
    (slotKey: string) =>
      ({
        file,
        callback,
      }: {
        file: File;
        callback: (r: { signedUrl: string }) => void | Promise<string | void>;
      }) => {
        return new Promise<{ signedUrl: string; fileKey: string }>(
          (resolve) => {
            createUploadUrl(file, (res: any) => {
              const fileKey = res?.fileKey ?? res?.file_key ?? "";
              const signedUrl = res?.signedUrl ?? res?.signed_url ?? "";
              const cdn_url = res?.cdn_url ?? "";
              uploadRefs.current[slotKey] = { fileKey, cdn_url };
              callback({ signedUrl });
              resolve({ signedUrl, fileKey });
            });
          }
        );
      },
    [createUploadUrl]
  );

  useEffect(() => {
    if (pages.length === 0 && instagramAccounts.length === 0) return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;

    const patch: Partial<CreativeBlock> = {};
    if (!firstCreative.fbPage && pages.length > 0) {
      const preferredPage =
        (defaults?.fb_page_id &&
          pages.find((p) => p.id === defaults.fb_page_id)) ??
        pages[0];
      if (preferredPage) patch.fbPage = preferredPage;
    }

    if (Object.keys(patch).length === 0) return;

    setValue(
      "creatives.0",
      { ...firstCreative, ...patch },
      { shouldValidate: true }
    );

    const newPageId = patch.fbPage?.id;
    if (
      newPageId &&
      !isEditMode &&
      String(draft.adAccountId ?? "").trim() &&
      onFetchInstagramForFacebookPage
    ) {
      void onFetchInstagramForFacebookPage(newPageId);
    }
  }, [
    pages,
    instagramAccounts,
    defaults?.fb_page_id,
    draft.adAccountId,
    getValues,
    isEditMode,
    onFetchInstagramForFacebookPage,
    setValue,
  ]);

  const handleFacebookPageChangeForInstagram = useCallback(
    async (facebookPageId: string) => {
      if (!onFetchInstagramForFacebookPage) return [];
      if (!String(draft.adAccountId ?? "").trim()) return [];
      setInstagramPageFetchLoading(true);
      try {
        const rows = await onFetchInstagramForFacebookPage(
          String(facebookPageId || "").trim()
        );
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      } finally {
        setInstagramPageFetchLoading(false);
      }
    },
    [draft.adAccountId, onFetchInstagramForFacebookPage]
  );

  const hasHydratedEditRef = useRef(false);

  useEffect(() => {
    setBudgetUpdatedByUser(false);
    applyingDefaultsBudgetRef.current = false;
    hasHydratedDefaultsRef.current = false;
    setHasCompletedInitialDefaultsFetch(false);
    hasHydratedEditRef.current = false;
  }, [draft.eventId, draft.adAccountId, isEditMode, editHydrationKey]);

  useEffect(() => {
    if (!isEditMode || !guidedEditPayload) return;
    if (hasHydratedEditRef.current) return;
    if (pages.length === 0) return;

    const igIdsNeeded = guidedEditPayloadInstagramIds(guidedEditPayload);
    if (igIdsNeeded.length > 0) {
      const igListReady = igIdsNeeded.every((id) =>
        instagramAccounts.some((ig) => ig.id === id)
      );
      if (!igListReady) return;
    }

    hasHydratedEditRef.current = true;
    const p = guidedEditPayload;
    const creatives: CreativeBlock[] = p.creatives.map((c) => ({
      name: c.name,
      primaryText: c.primary_text.slice(0, PRIMARY_TEXT_MAX),
      squareMediaUrl: c.square_url,
      squareMediaFileKey: c.square_file_key ?? undefined,
      squareMediaType: c.square_media_type,
      verticalMediaUrl: c.portrait_url,
      verticalMediaFileKey: c.portrait_file_key ?? undefined,
      verticalMediaType: c.portrait_media_type,
      fbPage: pages.find((pg) => pg.id === c.fb_page_id) ?? null,
      instagramAccount:
        instagramAccounts.find((ig) => ig.id === c.instagram_id) ?? null,
    }));
    setValue("creatives", creatives, { shouldValidate: true });
    setValue(
      "budget",
      {
        type: p.budget.type,
        amount: {
          amount: p.budget.amount / 100,
          currency: "USD",
        },
      },
      { shouldValidate: true }
    );
    setValue(
      "schedule",
      {
        start_at: wireScheduleToEventWall(
          p.schedule.start_at,
          draft.eventTimezone
        ),
        end_at: wireScheduleToEventWall(p.schedule.end_at, draft.eventTimezone),
      },
      { shouldValidate: true }
    );
    setValue("smart_targeting", !guidedEditPayloadImpliesManualTargeting(p), {
      shouldValidate: true,
    });
    if (p.targeting.custom_audiences?.length) {
      setValue(
        "custom_audiences",
        p.targeting.custom_audiences as AudienceData[],
        { shouldValidate: true }
      );
    }
    const loc = p.targeting.locations;
    if (loc) {
      if ((loc.countries?.length ?? 0) > 0) {
        setValue("countries", loc.countries ?? [], { shouldValidate: true });
      }
      if ((loc.geo_markets?.length ?? 0) > 0) {
        setValue(
          "geo_markets",
          (loc.geo_markets ?? []).map((g) => ({
            key: g.key,
            value: g.value,
            country: g.country ?? "",
            name: ("name" in g && (g as { name?: string }).name) || g.value,
          })),
          { shouldValidate: true }
        );
      }
      if ((loc.regions?.length ?? 0) > 0) {
        setValue("regions", loc.regions ?? [], { shouldValidate: true });
      }
      if ((loc.cities?.length ?? 0) > 0) {
        setValue("cities", loc.cities ?? [], { shouldValidate: true });
      }
    }
    const demo = p.targeting.demographics;
    if (demo) {
      if (demo.age_min != null) {
        setValue("age_min", demo.age_min, { shouldValidate: true });
      }
      if (demo.age_max != null) {
        setValue("age_max", demo.age_max, { shouldValidate: true });
      }
      if (demo.genders != null && demo.genders !== "") {
        setValue("genders", demo.genders, { shouldValidate: true });
      }
    }
    if ((p.targeting.interests?.length ?? 0) > 0) {
      setValue("interests", p.targeting.interests ?? [], {
        shouldValidate: true,
      });
    }
  }, [
    isEditMode,
    guidedEditPayload,
    editHydrationKey,
    pages,
    instagramAccounts,
    setValue,
    draft.eventTimezone,
  ]);

  useEffect(() => {
    if (isEditMode) return;
    const id = defaults?.ad_account_id?.trim();
    if (!id) return;
    onGuidedCreateAdAccountResolved?.(id);
  }, [isEditMode, defaults?.ad_account_id, onGuidedCreateAdAccountResolved]);

  useEffect(() => {
    if (isEditMode) return;
    if (draft.mode !== "create") return;
    if (!draft.eventId) return;
    const currentBudgetType = budget?.type === "daily" ? "daily" : "lifetime";
    const isBudgetDebounced =
      debouncedBudgetCents === budgetCents &&
      debouncedBudgetType === currentBudgetType;
    if (budgetUpdatedByUser && !isBudgetDebounced) return;
    if (applyingDefaultsBudgetRef.current) {
      applyingDefaultsBudgetRef.current = false;
      return;
    }

    const shouldUseBudget = budgetUpdatedByUser && debouncedBudgetCents != null;
    const isInitialDefaultsFetch = !hasCompletedInitialDefaultsFetch;
    setLoadingDefaults(true);
    actions.ADMIN.fetchCampaignDefaults({
      event_id: draft.eventId,
      ...(draft.mode === "create" && draft.adAccountId
        ? { ad_account_id: draft.adAccountId }
        : {}),
      ...(shouldUseBudget
        ? {
            budget: debouncedBudgetCents,
            budget_type: debouncedBudgetType,
          }
        : {}),
    })
      .then((res: any) => {
        setDefaults((prev) => {
          const isFirstHydration = !hasHydratedDefaultsRef.current || !prev;
          if (isFirstHydration) {
            hasHydratedDefaultsRef.current = true;
            return res;
          }

          if (!getValues("smart_targeting") || !res?.targeting) {
            return prev;
          }

          return {
            ...prev,
            targeting: res.targeting,
          };
        });
      })
      .catch((err: any) => {
        setDefaults(null);
        const message =
          typeof err?.message === "string" ? err.message.trim() : "";
        toast({
          title: "Unable to create campaign",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        onBack();
      })
      .finally(() => {
        setLoadingDefaults(false);
        if (isInitialDefaultsFetch) {
          setHasCompletedInitialDefaultsFetch(true);
        }
      });
  }, [
    isEditMode,
    draft.eventId,
    draft.mode,
    draft.adAccountId,
    debouncedBudgetCents,
    debouncedBudgetType,
    budgetUpdatedByUser,
    budgetCents,
    budget?.type,
    hasCompletedInitialDefaultsFetch,
    onBack,
    toast,
  ]);

  useEffect(() => {
    if (!defaults?.budget) return;
    const nextType = defaults.budget.type;
    const nextAmount = defaults.budget.amount / 100;
    const currentBudget = getValues("budget");

    if (
      currentBudget?.type === nextType &&
      currentBudget?.amount?.amount === nextAmount
    ) {
      return;
    }

    applyingDefaultsBudgetRef.current = true;
    setValue(
      "budget",
      {
        type: nextType,
        amount: {
          amount: nextAmount,
          currency: currentBudget?.amount?.currency ?? "USD",
        },
      },
      { shouldValidate: true }
    );
  }, [defaults?.budget, getValues, setValue]);

  useEffect(() => {
    const templates = defaults?.primary_text_templates;
    if (!templates?.length) return;

    const firstTemplate = templates[0];
    if (!firstTemplate || typeof firstTemplate !== "string") return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;
    if (firstCreative.primaryText.trim().length > 0) return;

    setValue(
      "creatives.0",
      {
        ...firstCreative,
        primaryText: firstTemplate.slice(0, PRIMARY_TEXT_MAX),
      },
      { shouldValidate: true }
    );
  }, [defaults?.primary_text_templates, getValues, setValue]);

  useEffect(() => {
    const images = defaults?.creative_images;
    if (!images?.length) return;

    const squareImage = images.find(
      (image) => image.type === "square" && image.src
    )?.src;
    const portraitImage = images.find(
      (image) => image.type === "portrait" && image.src
    )?.src;

    if (!squareImage && !portraitImage) return;

    const firstCreative = getValues("creatives.0");
    if (!firstCreative) return;

    const shouldPrefillSquare = !firstCreative.squareMediaUrl && !!squareImage;
    const shouldPrefillPortrait =
      !firstCreative.verticalMediaUrl && !!portraitImage;

    if (!shouldPrefillSquare && !shouldPrefillPortrait) return;

    setValue(
      "creatives.0",
      {
        ...firstCreative,
        name: firstCreative.name || 'Ad 1',
        ...(shouldPrefillSquare
          ? { squareMediaUrl: squareImage, squareMediaType: "image" as const }
          : {}),
        ...(shouldPrefillPortrait
          ? {
              verticalMediaUrl: portraitImage,
              verticalMediaType: "image" as const,
            }
          : {}),
      },
      { shouldValidate: true }
    );
  }, [defaults?.creative_images, getValues, setValue]);

  useEffect(() => {
    const defaultSchedule = defaults?.schedule;
    if (!defaultSchedule?.start_at || !defaultSchedule?.end_at) return;

    const currentSchedule = getValues("schedule");

    if (!currentSchedule.start_at) {
      setValue(
        "schedule.start_at",
        wireScheduleToEventWall(defaultSchedule.start_at, draft.eventTimezone),
        { shouldValidate: true }
      );
    }

    if (!currentSchedule.end_at) {
      setValue(
        "schedule.end_at",
        wireScheduleToEventWall(defaultSchedule.end_at, draft.eventTimezone),
        { shouldValidate: true }
      );
    }
  }, [defaults?.schedule, draft.eventTimezone, getValues, setValue]);

  const pageOptions = pages.map((p) => ({ label: p.name, value: p }));
  const instagramOptions = instagramAccounts.map((i) => ({
    label: i.username,
    value: i,
  }));

  const addCreative = useCallback(() => {
    const nextName = `Ad ${creatives.length + 1}`;
    const templates = defaults?.primary_text_templates ?? [];
    const nextTemplate =
      templates.length > 0
        ? templates[creatives.length % templates.length]
        : "";

    append({
      name: nextName,
      primaryText: nextTemplate.slice(0, PRIMARY_TEXT_MAX),
      fbPage: creatives[0]?.fbPage ?? pages[0] ?? null,
      instagramAccount: null,
    });
    setEditingCreativeName(null);
  }, [append, creatives, defaults?.primary_text_templates, pages]);

  const updateCreative = useCallback(
    (index: number, patch: Partial<CreativeBlock>) => {
      const creative = getValues(`creatives.${index}`);
      if (!creative) return;

      const merged = { ...creative, ...patch };
      setValue(`creatives.${index}`, merged, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [getValues, setValue]
  );

  const removeCreative = useCallback(
    (index: number) => {
      if (creatives.length <= 1) return;
      if (editingCreativeName?.index === index) {
        setEditingCreativeName(null);
      } else if (
        editingCreativeName != null &&
        editingCreativeName.index > index
      ) {
        setEditingCreativeName({
          ...editingCreativeName,
          index: editingCreativeName.index - 1,
        });
      }
      remove(index);
    },
    [creatives.length, editingCreativeName, remove]
  );

  const ctaText = "Buy Tickets";
  const eventName = draft.event?.name ?? "Event";
  const reviewAdAccountId = draft.adAccountId ?? defaults?.ad_account_id ?? "";
  const adAccountName = reviewAdAccountId
    ? adAccounts.find((a) => a.id === reviewAdAccountId)?.name ??
      reviewAdAccountId
    : reviewAdAccountId ?? "—";
  const eventDescription =
    (draft.event as { description?: string } | undefined)?.description ?? "";
  const websiteUrl = draft.event?.link ?? "";
  const creativeMediaValidationMessages = useMemo(
    () =>
      creatives.map((creative) =>
        hasMixedGuidedCreativeMediaTypes(creative)
          ? MATCHING_CREATIVE_MEDIA_TYPES_ERROR
          : null
      ),
    [creatives]
  );

  const isCreativeSectionComplete = !errors.creatives;
  const hasRequiredAudience =
    !showAudienceSection ||
    isSmartTargetingAvailable ||
    hasManualAudienceTargetingSignal({
      custom_audiences: customAudiences,
      interests,
      countries,
      geo_markets: geoMarkets,
      regions,
      cities,
      age_min: ageMin,
      age_max: ageMax,
      genders,
    });
  const isAudienceSectionComplete =
    hasRequiredAudience && (isSmartTargetingAvailable || !errors.custom_audiences);
  const isBudgetSectionComplete =
    !errors.budget &&
    !errors.schedule &&
    Boolean(scheduleStart) &&
    Boolean(scheduleEnd);
  const shouldSkipReviewStep =
    draft.mode === "edit" ||
    draft.mode === "edit_new_creative" ||
    draft.mode === "edit_new_adset";

  const targetingForReview: {
    strategy: "template" | "custom";
    resolved_strategies:
      | { id?: string; name?: string; description?: string }[]
      | AudienceData[];
  } = {
    strategy: "custom",
    resolved_strategies: customAudiences ?? [],
  };
  if (isSmartTargetingAvailable && defaults?.targeting) {
    targetingForReview.strategy = "template";
    targetingForReview.resolved_strategies =
      defaults.targeting.resolved_strategies ?? [];
  }

  const handleLaunchCampaign = useCallback(async () => {
    const valid = await trigger();
    if (!valid) {
      setStep("edit");
      return;
    }

    setLaunching(true);
    try {
      const payload = buildCreateCampaignPayload(draft, getValues());
      const campaignId = await handleCreateCampaign(payload);
      if (campaignId && typeof campaignId === "object") {
        onLaunchSuccess(campaignId);
      } else {
        onLaunchSuccess();
      }
    } catch (err: any) {
      toast({
        title: "Failed to launch campaign",
        description: err?.message ?? String(err),
        status: "error",
      });
    } finally {
      setLaunching(false);
    }
  }, [draft, getValues, handleCreateCampaign, onLaunchSuccess, toast, trigger]);

  const handleSaveAdset = useCallback(async () => {
    if (!shouldSkipReviewStep && step !== "review") {
      setStep("review");
      return;
    }
    const valid = await trigger();
    const values = getValues();
    if (draft.mode === "create") {
      return;
    }
    const { guidedSubmitParams } = buildSaveAdsetParams({
      draft,
      values,
      isNewCreativeMode,
      showBudgetSection,
      showAudienceSection,
      addToAllCreativeAdsets,
      selectableCreativeTargetAdsetIds,
      selectedCreativeTargetAdsetIds,
    });
    if (!valid || !handleSubmitGuidedEdit) {
      if (!valid) setStep("edit");
      return;
    }
    if (!guidedSubmitParams) return;

    setLaunching(true);
    try {
      await handleSubmitGuidedEdit(guidedSubmitParams);
      onLaunchSuccess();
    } catch (err: any) {
      let saveErrorTitle = "Failed to update ad set";
      if (draft.mode === "edit_new_adset") {
        saveErrorTitle = "Failed to create ad set";
      }
      toast({
        title: saveErrorTitle,
        description: err?.message ?? String(err),
        status: "error",
      });
    } finally {
      setLaunching(false);
    }
  }, [
    addToAllCreativeAdsets,
    draft,
    getValues,
    handleSubmitGuidedEdit,
    isNewCreativeMode,
    onLaunchSuccess,
    selectableCreativeTargetAdsetIds,
    selectedCreativeTargetAdsetIds,
    shouldSkipReviewStep,
    step,
    setStep,
    toast,
    trigger,
  ]);

  const handleReviewCampaign = useCallback(async () => {
    const valid = await trigger();
    if (!valid || !hasRequiredAudience || !hasRequiredCreativeTargetAdsets)
      return;
    setStep("review");
  }, [hasRequiredAudience, hasRequiredCreativeTargetAdsets, trigger]);

  const isBlockingDataLoading =
    isRelatedDataLoading ||
    (loadingDefaults && !hasCompletedInitialDefaultsFetch);
  const isSaveBlockedByIdentityLoading =
    isRelatedDataLoading || instagramPageFetchLoading;
  const loadingModal = (
    <Modal isOpen={isBlockingDataLoading} onClose={() => {}} isCentered>
      <ModalOverlay />
      <ModalContent
        w="220px"
        py={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.800"
        borderRadius="xl"
      >
        <VStack spacing={3}>
          <KYDLoading withText="Loading campaign data..." />
        </VStack>
      </ModalContent>
    </Modal>
  );

  const normalizedPublishStatusText =
    loadingMessageToText(publishStatusText ?? "") ?? publishStatusText;
  const trimmedPublishStatus =
    typeof normalizedPublishStatusText === "string"
      ? normalizedPublishStatusText.trim()
      : normalizedPublishStatusText;
  /** Shown while the PUT runs, before parent sets publishStatusText (e.g. "Publishing edit…"). */
  const publishStatusWhileAwaitingRequest =
    launching && (handleSubmitGuidedEdit || handleCreateCampaign)
      ? "Submitting…"
      : null;
  const displayPublishStatusText =
    trimmedPublishStatus || publishStatusWhileAwaitingRequest;
  const isApplyingPublishChanges = launching || Boolean(trimmedPublishStatus);
  const showPublishStatusOnReview =
    step === "review" && Boolean(displayPublishStatusText);
  const publishStatusToast =
    (launching && displayPublishStatusText) || showPublishStatusOnReview ? (
      <Box
        position="fixed"
        bottom={6}
        right={6}
        zIndex={1401}
        bg="rgba(74, 85, 104, 0.85)"
        color="white"
        px={4}
        py={3}
        borderRadius="md"
        shadow="lg"
      >
        <Text fontSize="sm" fontWeight="medium">
          {displayPublishStatusText}
        </Text>
      </Box>
    ) : null;

  const leaveEditorModal = (
    <Modal
      isOpen={isExitConfirmOpen}
      onClose={() => setIsExitConfirmOpen(false)}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Leave editor?</ModalHeader>
        <ModalBody>
          <Text>
            All changes made to this campaign will be lost, are you sure you
            want to leave the editor?
          </Text>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              bg="black"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => setIsExitConfirmOpen(false)}
            >
              No
            </Button>
            <Button
              bg="white"
              color="black"
              borderWidth="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.100" }}
              onClick={() => {
                setIsExitConfirmOpen(false);
                onBack();
              }}
            >
              Yes
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const reviewEvent = buildReviewEvent(draft.event);
  const reviewLaunchHandler =
    isEditMode && handleSubmitGuidedEdit
      ? handleSaveAdset
      : handleLaunchCampaign;
  const reviewLaunchLabel = isEditMode ? "Save changes" : undefined;
  const reviewLaunchLoadingText = isEditMode ? "Saving…" : undefined;

  if (step === "review") {
    return (
      <>
        <CreateCampaignReview
          event={reviewEvent}
          adAccountName={adAccountName}
          budget={budget as BudgetData}
          scheduleStart={scheduleStart}
          scheduleEnd={scheduleEnd}
          scheduleEventTimezone={draft.eventTimezone}
          locationLabel={
            [draft.event?.venue?.name, draft.event?.venue?.address]
              .filter(Boolean)
              .join(", ") || undefined
          }
          targeting={targetingForReview}
          creatives={(creatives ?? []) as CreativeBlock[]}
          onBackToEditor={handleBackToEditor}
          onLaunch={reviewLaunchHandler}
          launching={launching}
          launchLabel={reviewLaunchLabel}
          launchLoadingText={reviewLaunchLoadingText}
          sectionVisibility={sectionVisibility.review}
          isIdentityDataLoading={isSaveBlockedByIdentityLoading}
        />
        {loadingModal}
        {publishStatusToast}
      </>
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <VStack align="stretch" spacing={4} w="full">
          {/* Mobile-only: Editor / Preview toggle */}
          <HStack
            display={{ base: "flex", md: "none" }}
            justify="center"
            w="full"
            py={2}
          >
            <HStack
              bg="gray.200"
              rounded="lg"
              p={1}
              spacing={0}
              role="tablist"
              aria-label="Editor or Preview"
            >
              <Button
                size="sm"
                variant="ghost"
                flex={1}
                minW="100px"
                rounded="md"
                bg={mobilePanel === "editor" ? "white" : "transparent"}
                shadow={mobilePanel === "editor" ? "sm" : "none"}
                onClick={() => setMobilePanel("editor")}
                fontWeight="medium"
                fontSize="sm"
              >
                Editor
              </Button>
              <Button
                size="sm"
                variant="ghost"
                flex={1}
                minW="100px"
                rounded="md"
                bg={mobilePanel === "preview" ? "white" : "transparent"}
                shadow={mobilePanel === "preview" ? "sm" : "none"}
                onClick={() => setMobilePanel("preview")}
                fontWeight="medium"
                fontSize="sm"
              >
                Preview
              </Button>
            </HStack>
          </HStack>

          <HStack align="stretch" spacing={6} w="full" flex={1} minH={0}>
            {/* Left panel – form card */}
            <Container
              flex={1}
              overflow="auto"
              bgColor="#fafafa"
              display={{
                base: mobilePanel === "editor" ? "block" : "none",
                md: "block",
              }}
            >
              <VStack align="stretch" spacing={0}>
                {/* Ad Creative */}
                <Box pb={8} borderBottomWidth="1px" borderColor="gray.100">
                  <SectionHeader
                    title="Ad Creative"
                    subtitle="Upload your ad media and text."
                    isComplete={isCreativeSectionComplete}
                    isCollapsed={!adCreativeOpen}
                    onToggle={() => setAdCreativeOpen((o) => !o)}
                  />
                  {adCreativeOpen && (
                    <VStack align="stretch" spacing={5} mt={4}>
                      {fields.map((field, index) => (
                        <CreativeBlockEditor
                          key={field.id}
                          creative={
                            (creatives[index] ?? {
                              name: `Ad ${index + 1}`,
                              primaryText: "",
                              fbPage: null,
                              instagramAccount: null,
                            }) as CreativeBlock
                          }
                          index={index}
                          totalCreatives={fields.length}
                          pageOptions={pageOptions}
                          instagramOptions={instagramOptions}
                          createUploadUrlForSlot={createUploadUrlForKYD}
                          uploadRefs={uploadRefs}
                          onUpdate={updateCreative}
                          onRemove={removeCreative}
                          onAddMore={addCreative}
                          onFacebookPageChange={
                            onFetchInstagramForFacebookPage
                              ? handleFacebookPageChangeForInstagram
                              : undefined
                          }
                          applyDefaultInstagramOnFacebookPageChange={
                            !isEditMode
                          }
                          editingName={editingCreativeName}
                          onEditingNameChange={setEditingCreativeName}
                          mediaValidationMessage={
                            creativeMediaValidationMessages[index]
                          }
                          selectContainerStyles={selectContainerStyles}
                          allowCreativeCountChanges={!isEditMode}
                          isDisabled={isApplyingPublishChanges}
                          isFacebookPagesLoading={isRelatedDataLoading}
                          isInstagramAccountsLoading={
                            instagramPageFetchLoading
                          }
                        />
                      ))}
                    </VStack>
                  )}
                </Box>

                {showAudienceSection && (
                  <Box py={4} borderBottomWidth="1px" borderColor="gray.100">
                    <SectionHeader
                      title="Audience Targeting"
                      isComplete={isAudienceSectionComplete}
                      isCollapsed={!audienceOpen}
                      onToggle={() => setAudienceOpen((o) => !o)}
                    />
                    {audienceOpen && (
                      <VStack align="stretch" spacing={4}>
                        {!isEditMode && (
                          <Box
                            py={4}
                            px={6}
                            bgColor="white"
                            rounded={"lg"}
                            border={"1px solid"}
                            borderColor="gray.200"
                          >
                            <HStack justify="space-between" w="full" mb={2}>
                              <VStack align="flex-start">
                                <HStack>
                                  <Text fontSize="md" fontWeight={"semibold"}>
                                    Smart Targeting
                                  </Text>
                                  <Badge
                                    color="green.100"
                                    backgroundColor="green.500"
                                    size="md"
                                    rounded={"md"}
                                    textTransform={"none"}
                                    fontWeight={"semibold"}
                                    px={"8px"}
                                    py={"2px"}
                                  >
                                    Recommended
                                  </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                  We use existing KYD data and Meta AI to find
                                  the best target audiences for this campaign.
                                </Text>
                              </VStack>
                              <Switch
                                size="lg"
                                isChecked={!!smartTargeting}
                                onChange={(e) => {
                                  setValue("smart_targeting", e.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }}
                              />
                            </HStack>
                          </Box>
                        )}
                        {!isSmartTargetingAvailable && (
                          <>
                            <AudienceCard
                              ad_account_id={
                                draft.adAccountId ??
                                defaults?.ad_account_id ??
                                ""
                              }
                              pages={pages.map((p) => ({
                                id: p.id,
                                name: p.name,
                              }))}
                              instagramAccounts={instagramAccounts.map((i) => ({
                                id: i.id,
                                username: i.username,
                              }))}
                              audiences={customAudiences}
                              isEmbedded={true}
                            />
                            <GuidedTargetingFields
                              search={search ?? undefined}
                              isLoading={isRelatedDataLoading}
                            />
                          </>
                        )}
                      </VStack>
                    )}
                  </Box>
                )}

                {showBudgetSection && (
                  <>
                    {/* Budget & Schedule */}
                    <Box py={4} borderBottomWidth="1px" borderColor="gray.100">
                      <SectionHeader
                        title="Budget & Schedule"
                        isComplete={isBudgetSectionComplete}
                        isCollapsed={!budgetOpen}
                        onToggle={() => setBudgetOpen((o) => !o)}
                      />
                      {budgetOpen && (
                        <Stack spacing={4} w="full" pt={4}>
                          <GuidedBudgetEditor
                            budget={budget as BudgetData}
                            onChangeBudgetType={(nextType) => {
                              setBudgetUpdatedByUser(true);
                              setValue("budget.type", nextType, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                            onChangeBudgetAmount={(amount) => {
                              setBudgetUpdatedByUser(true);
                              setValue("budget.amount.amount", amount, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                          />

                          <GuidedScheduleEditor
                            eventTimezone={draft.eventTimezone}
                            startAt={scheduleStart}
                            endAt={scheduleEnd}
                            onChangeStartAt={(value) =>
                              setValue("schedule.start_at", value, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            }
                            onChangeEndAt={(value) =>
                              setValue("schedule.end_at", value, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            }
                          />
                        </Stack>
                      )}
                    </Box>
                  </>
                )}

                {isNewCreativeMode && (
                  <CreativeAdsetTargetingCard
                    adsets={creativeTargetAdsets}
                    selectedAdsetIds={selectedCreativeTargetAdsetIds}
                    addToAllAdsets={addToAllCreativeAdsets}
                    isAddToAllAdsetsDisabled={isApplyingPublishChanges}
                    onToggleAddToAllAdsets={(checked) => {
                      setAddToAllCreativeAdsets(checked);
                      if (checked) {
                        setSelectedCreativeTargetAdsetIds(
                          selectableCreativeTargetAdsetIds
                        );
                        return;
                      }
                      setSelectedCreativeTargetAdsetIds(
                        selectableCreativeTargetAdsetIds.length > 0
                          ? [selectableCreativeTargetAdsetIds[0]]
                          : []
                      );
                    }}
                    onToggleAdset={(adsetId, checked) => {
                      setSelectedCreativeTargetAdsetIds((prev) => {
                        if (checked)
                          return Array.from(new Set([...prev, adsetId]));
                        return prev.filter((id) => id !== adsetId);
                      });
                    }}
                  />
                )}

                {/* Review Campaign button */}
                <HStack mt={4} justify="space-between" w="full">
                  <Button
                    variant="ghost"
                    size="md"
                    bgColor="white"
                    onClick={() => setIsExitConfirmOpen(true)}
                  >
                    <FaChevronLeft style={{ marginRight: "8px" }} />
                    Back
                  </Button>
                  <Button
                    bgColor="gray.900"
                    color="white"
                    _disabled={{
                      bgColor: "gray.300",
                      color: "white",
                      _hover: { bgColor: "gray.300" },
                      cursor: "not-allowed",
                    }}
                    rightIcon={<ChevronRightIcon />}
                    onClick={
                      shouldSkipReviewStep
                        ? handleSaveAdset
                        : handleReviewCampaign
                    }
                    isDisabled={
                      !isValid ||
                      !hasRequiredAudience ||
                      !hasRequiredCreativeTargetAdsets ||
                      launching ||
                      isSaveBlockedByIdentityLoading
                    }
                    isLoading={launching}
                    size="md"
                  >
                    {shouldSkipReviewStep ? "Publish" : "Review Campaign"}
                  </Button>
                </HStack>
              </VStack>
            </Container>

            <CreateCampaignFlowPreview
              mobilePanel={mobilePanel}
              creatives={(creatives ?? []) as CreativeBlock[]}
              fieldIds={fields.map((field) => field.id)}
              ctaText={ctaText}
              websiteUrl={websiteUrl}
              eventName={eventName}
              eventDescription={eventDescription}
            />
          </HStack>
        </VStack>
      </FormProvider>
      {loadingModal}
      {publishStatusToast}
      {leaveEditorModal}
    </>
  );
}
